<template>
  <nav
    class="bottom-nav"
    :aria-label="$t('crudmanager.bottomNav.title')">
    <template v-if="resolvedItems.length === 0">
      <button
        type="button"
        class="bottom-nav-empty"
        @click="goToSettings">
        <q-icon
          :name="symRoundedTune"
          class="bottom-nav-empty__icon" />
        <span>{{ $t('crudmanager.bottomNav.empty') }}</span>
      </button>
    </template>
    <template v-else>
      <BottomNavItem
        v-for="item in visibleSlots"
        :key="item.id"
        :item="item"
        @long-press="goToSettings" />
      <button
        v-if="overflowItems.length > 0"
        type="button"
        class="bottom-nav-item"
        :aria-label="$t('crudmanager.bottomNav.more')"
        @click="moreOpen = true"
        @pointerdown="onMorePointerDown"
        @pointerup="clearMoreLongPress"
        @pointerleave="clearMoreLongPress"
        @pointercancel="clearMoreLongPress">
        <q-icon
          :name="symRoundedMoreHoriz"
          class="bottom-nav-item__icon" />
        <span class="bottom-nav-item__label">{{ $t('crudmanager.bottomNav.more') }}</span>
      </button>
    </template>
  </nav>
  <BottomNavMoreSheet
    v-model="moreOpen"
    :items="overflowItems" />
</template>

<script setup lang="ts">
import { symRoundedMoreHoriz, symRoundedTune } from '@quasar/extras/material-symbols-rounded'
import BottomNavItem from 'src/components/bottom-nav/BottomNavItem.vue'
import BottomNavMoreSheet from 'src/components/bottom-nav/BottomNavMoreSheet.vue'
import { useBottomNavSource } from 'src/composables/useBottomNavSource'
import { onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const { resolvedItems, visibleSlots, overflowItems } = useBottomNavSource()

const moreOpen = ref(false)
const moreLongPressTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const moreLongPressFired = ref(false)
const LONG_PRESS_MS = 500

const clearMoreLongPress = () => {
  if (moreLongPressTimer.value) {
    clearTimeout(moreLongPressTimer.value)
    moreLongPressTimer.value = null
  }
}

const onMorePointerDown = () => {
  moreLongPressFired.value = false
  clearMoreLongPress()
  moreLongPressTimer.value = setTimeout(() => {
    moreLongPressFired.value = true
    moreLongPressTimer.value = null
    goToSettings()
  }, LONG_PRESS_MS)
}

const goToSettings = () => {
  void router.push({ path: '/settings', hash: '#bottom-nav' })
}

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (moreOpen.value) moreOpen.value = false
  },
)

onBeforeUnmount(clearMoreLongPress)
</script>

<style lang="scss" scoped>
.bottom-nav {
  display: flex;
  align-items: stretch;
  justify-content: space-around;
  width: 100%;
  height: 56px;
  padding: 0 4px;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: var(--q-dark);
  background: color-mix(in srgb, var(--q-primary) 95%, #000);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
}

.body--light .bottom-nav {
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.05);
}

.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1 1 0;
  min-width: 0;
  padding: 6px 4px;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.78);
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  transition:
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: #fff;
    background-color: rgba(255, 255, 255, 0.06);
  }
}

.body--light .bottom-nav-item {
  color: rgba(0, 0, 0, 0.7);

  &:hover {
    color: #000;
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.bottom-nav-item__icon {
  font-size: 1.45rem;
}

.bottom-nav-item__label {
  font-size: 0.66rem;
  font-weight: 550;
  line-height: 1.1;
  letter-spacing: 0.01em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: capitalize;
}

.bottom-nav-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1 1 0;
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.85);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 550;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.body--light .bottom-nav-empty {
  color: rgba(0, 0, 0, 0.78);
}

.bottom-nav-empty__icon {
  font-size: 1.25rem;
}
</style>
